/**
 * PostOverlay - Mobile slide-in for a single post.
 *
 * Mirrors the MessagesOverlay pattern: tapping a post in the feed opens this
 * overlay on top of the current route without changing the URL, so the back
 * button (header button, Escape, browser back, or swipe-to-close) returns the
 * user to the feed they came from.
 *
 * On desktop (md+), SlideOverlay is display:none and the caller should
 * navigate to `/posts/:id` instead — the underlying `<Post />` route is
 * unchanged and continues to handle direct links on every viewport.
 */

import React, { useEffect, useState } from 'react';
import { getPostById } from '../helpers/postsHelpers';
import SlideOverlay from './SlideOverlay';
import PostFeedItem from '../components/PostFeed/PostFeedItem';

function PostOverlay({ postId, onClose }) {
  const [post, setPost] = useState(null);
  const [commentsAccess, setCommentsAccess] = useState('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const open = postId != null;

  useEffect(() => {
    if (!open) {
      setPost(null);
      setCommentsAccess('full');
      setLoading(false);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    getPostById(postId)
      .then((data) => {
        if (cancelled) return;
        if (!data || (data.access !== 'full' && data.access !== 'anon_preview') || !data.post) {
          setError(true);
          setPost(null);
          return;
        }
        setPost(data.post);
        setCommentsAccess(data.comments_access || 'full');
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, postId]);

  return (
    <SlideOverlay
      open={open}
      title="Post"
      onClose={onClose}
      testId="post-overlay"
      zIndex={42}
    >
      <div className="p-4" data-testid="post-overlay-body">
        {loading && <p className="text-silver text-sm">Loading...</p>}
        {!loading && error && (
          <p className="text-silver text-sm">This post is unavailable.</p>
        )}
        {!loading && !error && post && (
          <PostFeedItem
            post={post}
            expandComments
            commentsAccess={commentsAccess}
            onDelete={() => onClose()}
          />
        )}
      </div>
    </SlideOverlay>
  );
}

export default PostOverlay;
